import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, X, Send, Loader2, ArrowLeft, Loader } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  isAdminUser, supportUnread, supportInbox, supportConversation, supportReply,
} from "@/lib/api";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
          " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function SupportBell() {
  const { user } = useAuth();
  const rec = user?.user_record_id || user?.record_id || "";
  const canUse = !!user && (isAdminUser(user) || user.approved === 1 || user.approved === true || String(user.approved || "") === "1");

  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [convos, setConvos] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [active, setActive] = useState(null); // {id, name, channel}
  const [channelFilter, setChannelFilter] = useState("all"); // all | chat | sms
  const shown = convos.filter((c) => channelFilter === "all" || (c.channel || "chat") === channelFilter);
  const unreadBy = (ch) => convos.filter((c) => (c.channel || "chat") === ch).reduce((a, c) => a + (c.unread || 0), 0);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Poll unread count for the bell badge.
  useEffect(() => {
    if (!canUse) return;
    let alive = true;
    const tick = () => supportUnread(rec).then((c) => alive && setCount(c)).catch(() => {});
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [canUse, rec]);

  const loadInbox = useCallback(() => {
    setLoadingList(true);
    supportInbox(rec).then(setConvos).catch(() => {}).finally(() => setLoadingList(false));
  }, [rec]);

  useEffect(() => {
    if (open && !active) loadInbox();
  }, [open, active, loadInbox]);

  // Poll the open conversation.
  const loadConvo = useCallback(() => {
    if (!active) return;
    supportConversation(active.id, rec).then(setMessages).catch(() => {});
  }, [active, rec]);

  useEffect(() => {
    if (!active) return;
    loadConvo();
    const id = setInterval(loadConvo, 8000);
    return () => clearInterval(id);
  }, [active, loadConvo]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const m = reply.trim();
    if (!m || sending || !active) return;
    setReply("");
    setMessages((x) => [...x, { id: `tmp-${Date.now()}`, message: m, sender: "admin", created_at: new Date().toISOString() }]);
    setSending(true);
    try {
      await supportReply(active.id, m, rec);
      await loadConvo();
    } catch {
      /* keep optimistic msg */
    } finally {
      setSending(false);
    }
  };

  if (!canUse) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative shrink-0 h-9 w-9 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-600"
        data-testid="support-bell"
        aria-label="Support inbox"
        title="Support inbox"
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="support-bell-count"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[9999] flex justify-end" data-testid="support-inbox">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setOpen(false); setActive(null); }} />
          <div className="relative w-full sm:w-[420px] h-[100dvh] bg-white shadow-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-violet-700 to-violet-900 text-white">
              {active ? (
                <button onClick={() => setActive(null)} className="p-1 hover:bg-white/10 rounded-lg" data-testid="support-back" aria-label="Back">
                  <ArrowLeft size={18} />
                </button>
              ) : (
                <Bell size={18} />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate" data-testid="support-header-title">
                  {active ? active.name : "Support Inbox"}
                </div>
                {!active && <div className="text-[11px] text-violet-200">{count} unread message{count === 1 ? "" : "s"}</div>}
              </div>
              <button onClick={() => { setOpen(false); setActive(null); }} className="p-1 hover:bg-white/10 rounded-lg" data-testid="support-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Conversation list */}
            {!active ? (
              <div className="flex-1 overflow-y-auto" data-testid="support-convo-list">
                <div className="flex gap-1 px-3 py-2 border-b border-zinc-100 bg-zinc-50 text-[11px] font-semibold" data-testid="support-channel-filter">
                  {[["all", "All"], ["chat", `Chat${unreadBy("chat") ? ` · ${unreadBy("chat")}` : ""}`], ["sms", `SMS${unreadBy("sms") ? ` · ${unreadBy("sms")}` : ""}`]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setChannelFilter(k)} className={`px-3 h-7 rounded-full ${channelFilter === k ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600"}`} data-testid={`support-filter-${k}`}>{l}</button>
                  ))}
                </div>
                {loadingList ? (
                  <div className="py-16 text-center text-zinc-400"><Loader2 className="animate-spin inline" size={18} /></div>
                ) : shown.length === 0 ? (
                  <div className="py-16 text-center text-zinc-400 text-sm">No conversations yet.</div>
                ) : (
                  shown.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setActive({ id: c.id, name: c.name, channel: c.channel }); setMessages([]); }}
                      className="w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 flex items-start gap-3"
                      data-testid={`support-convo-${c.id}`}
                    >
                      <div className="h-9 w-9 shrink-0 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold text-sm">
                        {(c.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm text-zinc-900 truncate">{c.name}</span>
                          <span className={`text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded shrink-0 ${c.channel === "sms" ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`} data-testid={`support-convo-channel-${c.id}`}>{c.channel === "sms" ? "SMS" : "Chat"}</span>
                          <span className="text-[10px] text-zinc-400 shrink-0">{fmtTime(c.updated_at)}</span>
                        </div>
                        <div className="text-xs text-zinc-500 truncate mt-0.5">{c.last_message}</div>
                      </div>
                      {c.unread > 0 && (
                        <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
                          {c.unread}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-[#f6f7f4]" data-testid="support-thread">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.sender === "admin" ? "bg-violet-600 text-white rounded-br-sm" : "bg-white border border-zinc-100 text-zinc-800 rounded-bl-sm"}`}>
                        <div>{m.message}</div>
                        <div className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-violet-200" : "text-zinc-400"}`}>{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={send} className="border-t border-zinc-200 p-2.5 flex items-center gap-2">
                  <input
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={active?.channel === "sms" ? "Text back (sent by SMS)…" : "Type your reply…"}
                    className="flex-1 h-10 rounded-full border border-zinc-200 px-4 text-sm focus:border-violet-400 outline-none"
                    data-testid="support-reply-input"
                  />
                  <button type="submit" disabled={sending || !reply.trim()} className="h-10 w-10 shrink-0 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white flex items-center justify-center" data-testid="support-reply-send">
                    {sending ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
